import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { accountingApi } from '../../services/accountingApi';
import { AccountingAccount, IncomeEntry } from '../../types/accounting';
import '../../pages/Accounting.css';

interface IncomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  income: IncomeEntry | null;
  accounts: AccountingAccount[];
}

const IncomeModal: React.FC<IncomeModalProps> = ({ isOpen, onClose, onSaved, income, accounts }) => {
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [incomeDate, setIncomeDate] = useState(new Date().toISOString().split('T')[0]);
  const [source, setSource] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (income) {
      setAccountId(String(income.account_id));
      setAmount(String(income.amount));
      setIncomeDate(income.income_date.split('T')[0]);
      setSource(income.source || '');
      setDescription(income.description || '');
    } else {
      setAccountId(accounts[0] ? String(accounts[0].id) : '');
      setAmount('');
      setIncomeDate(new Date().toISOString().split('T')[0]);
      setSource('');
      setDescription('');
    }
    setErrors({});
  }, [isOpen, income, accounts]);

  if (!isOpen) return null;

  const validate = () => {
    const next: Record<string, string> = {};
    if (!accountId) next.accountId = 'Account is required';
    if (!amount || parseFloat(amount) <= 0) next.amount = 'Amount must be greater than 0';
    if (!incomeDate) next.incomeDate = 'Date is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      accountId: parseInt(accountId),
      amount: parseFloat(amount),
      incomeDate,
      source: source.trim() || undefined,
      description: description.trim() || undefined
    };

    try {
      setLoading(true);
      if (income) {
        await accountingApi.updateIncomeEntry(income.id, payload);
        toast.success('Income entry updated successfully');
      } else {
        await accountingApi.createIncomeEntry(payload);
        toast.success('Income recorded successfully');
      }
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.error || 'Failed to save income entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="acc-modal-overlay" onClick={onClose}>
      <div className="acc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acc-modal-header">
          <h2>{income ? 'Edit Income Entry' : 'Add Income'}</h2>
          <button className="acc-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="acc-modal-body">
          <form onSubmit={handleSubmit}>
            <p style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: 0 }}>
              Use this for income that isn't already captured by a student invoice (e.g. donations, other one-off income).
            </p>
            <div className="acc-form-row">
              <div className="acc-form-group">
                <label>Account <span className="acc-required">*</span></label>
                <select className={errors.accountId ? 'error' : ''} value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={loading}>
                  <option value="">Select account</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {errors.accountId && <div className="acc-error-message">{errors.accountId}</div>}
              </div>
              <div className="acc-form-group">
                <label>Amount (AED) <span className="acc-required">*</span></label>
                <input type="number" step="0.01" min="0" className={errors.amount ? 'error' : ''} value={amount} onChange={(e) => setAmount(e.target.value)} disabled={loading} placeholder="0.00" />
                {errors.amount && <div className="acc-error-message">{errors.amount}</div>}
              </div>
            </div>

            <div className="acc-form-row">
              <div className="acc-form-group">
                <label>Date <span className="acc-required">*</span></label>
                <input type="date" className={errors.incomeDate ? 'error' : ''} value={incomeDate} onChange={(e) => setIncomeDate(e.target.value)} disabled={loading} max={new Date().toISOString().split('T')[0]} />
                {errors.incomeDate && <div className="acc-error-message">{errors.incomeDate}</div>}
              </div>
              <div className="acc-form-group">
                <label>Source</label>
                <input type="text" value={source} onChange={(e) => setSource(e.target.value)} disabled={loading} placeholder="e.g. donation, refund" maxLength={200} />
              </div>
            </div>

            <div className="acc-form-group">
              <label>Description / Notes</label>
              <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} disabled={loading} maxLength={500} placeholder="Optional details" />
            </div>

            <div className="acc-form-actions">
              <button type="button" className="acc-btn acc-btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
              <button type="submit" className="acc-btn acc-btn-primary" disabled={loading}>{loading ? 'Saving...' : (income ? 'Save Changes' : 'Add Income')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default IncomeModal;
